import { useCallback } from 'react';
import { useAuth } from '../auth';
import { useAppDispatch, useAppSelector } from '../store';
import {
  addOpsConfig,
  setAvailableOps,
  setCurrentOps,
} from '../store/slices/opsSlice';
import { OpsType } from '../types';
import { getOpsColor } from '../utils/ops-color';

export function useOps() {
  const dispatch = useAppDispatch();
  const { currentOps, availableOps, opsConfigs } = useAppSelector(
    (state) => state.ops
  );
  const { user } = useAuth();

  const switchOps = useCallback(
    (ops: OpsType) => {
      if (availableOps.includes(ops)) {
        dispatch(setCurrentOps(ops));
        localStorage.setItem('currentOps', ops);
      }
    },
    [dispatch, availableOps]
  );

  const initializeUserOps = useCallback(() => {
    // Prevent re-initialization if already initialized
    if (availableOps.length > 0 && currentOps) {
      return;
    }

    if (user && user.opsAssociations.length > 0) {
      const userOps = user.opsAssociations;
      dispatch(setAvailableOps(userOps));

      // For any new ops not in config, create a default config
      Array.from(userOps).forEach((ops) => {
        if (!opsConfigs[ops]) {
          dispatch(
            addOpsConfig({
              ops,
              config: {
                name: ops,
                displayName: ops.charAt(0).toUpperCase() + ops.slice(1),
                color: getOpsColor(ops),
                // dbName and screens will be fetched from API
              },
            })
          );
        }
      });

      // Set current ops from localStorage or first available
      const savedOps = localStorage.getItem('currentOps');
      const initialOps =
        savedOps && userOps.includes(savedOps) ? savedOps : userOps[0];

      // Persist it too — apiClient reads `ops` from localStorage, so leaving
      // it unset here makes Redux and localStorage disagree.
      localStorage.setItem('currentOps', initialOps);
      dispatch(setCurrentOps(initialOps));
    }
  }, [dispatch, user, currentOps, availableOps, opsConfigs]);

  const getCurrentOpsConfig = useCallback(() => {
    if (!currentOps) return null;
    return opsConfigs[currentOps] || null;
  }, [currentOps, opsConfigs]);

  const getOpsConfig = useCallback(
    (ops: OpsType) => {
      return opsConfigs[ops] || null;
    },
    [opsConfigs]
  );

  const hasMultipleOps = availableOps.length > 1;

  return {
    currentOps,
    availableOps,
    switchOps,
    initializeUserOps,
    getCurrentOpsConfig,
    getOpsConfig,
    hasMultipleOps,
  };
}
